// Output builders for cold-start and warm incremental compile results.
// Example: both paths share CSS assembly, warnings, and final size-limit checks.
import { buildResourceLimitWarning, throwResourceLimitError } from '../resource-limits'
import { buildRootVariableRule } from '../variables/regular-variable'
import { splitHeaderRules } from '../output/rule-order'
import { renderCssFromLeaves, renderLeafRule } from '../renderer'
import { collectDebugRules } from './debug'
import type { LuisCompileObservedRule, LuisCompileResult } from '../types'
import type { IncrementalCompileResultContext, LuisIncrementalCompileOutput } from './compile-types'
import type { LuisIncrementalCompileCache, LuisIncrementalCompileStats } from './types'

// Build the shared compile result used by cold-start and warm incremental paths.
export function buildIncrementalCompileResult(context: IncrementalCompileResultContext): LuisCompileResult {
  const cssRules = renderCssFromLeaves(context.rootDeclarations, context.preparedLeaves.regularLeaves, context.ruleOverridesByKey)
  const observedRules = context.preparedLeaves.observedLeaves.map((leaf) => buildObservedRule(leaf, context.ruleOverridesByKey))
  const initialObservedRules = context.preparedLeaves.initialObservedLeaves.map((leaf) => buildObservedRule(leaf, context.ruleOverridesByKey))
  const rootVariableRule = buildRootVariableRule(context.variables)
  const [headerRules, bodyRules] = splitHeaderRules(cssRules)
  const debugRules = collectDebugRules(context.preparedLeaves.allLeaves, context.sourceId, context.lineOffset, context.debugEnabled)
  const result: LuisCompileResult = {
    css: [...headerRules, rootVariableRule, ...bodyRules].filter(Boolean).join('\n\n'),
    headerRules,
    rootVariableRule,
    mainRules: context.preparedLeaves.mainRules,
    variables: context.variables,
    classVariables: context.classVariables,
    objectVariables: context.objectVariables,
    arrayVariables: context.arrayVariables,
    warnings: context.collectWarnings ? [...context.importWarnings, ...context.normalizeWarnings, ...context.preprocessWarnings, ...context.renderWarnings, ...context.resourceWarnings] : [],
    debugRules: context.debugEnabled ? debugRules : undefined,
    observedRules,
    initialObservedRules,
  }
  enforceCssSizeBudget(result, context.sourceId, context.resourcePolicy, context.resourceLimits.maxOutputCssChars)
  // Return the assembled incremental compile result.
  return result
}

// Build cold-start incremental output with an empty block cache.
export function buildColdStartCacheOutput(
  result: LuisCompileResult,
  mode: LuisIncrementalCompileStats['mode'],
  preparedLeaves: IncrementalCompileResultContext['preparedLeaves'],
  parseCache: LuisIncrementalCompileCache['parseCache'],
  parser: LuisIncrementalCompileStats['parser'],
): LuisIncrementalCompileOutput {
  // Return cold-start output with an empty block cache and zero reused blocks.
  return { result, cache: { blocks: [], parseCache }, stats: { mode, totalBlocks: preparedLeaves.allLeaves.length, reusedBlocks: 0, renderedBlocks: preparedLeaves.allLeaves.length, parser } }
}

// Build warm incremental output with reused block-cache entries.
export function buildWarmCacheOutput(
  result: LuisCompileResult,
  mode: LuisIncrementalCompileStats['mode'],
  preparedLeaves: IncrementalCompileResultContext['preparedLeaves'],
  nextBlocks: LuisIncrementalCompileCache['blocks'],
  parseCache: LuisIncrementalCompileCache['parseCache'],
  reusedBlocks: number,
  parser: LuisIncrementalCompileStats['parser'],
): LuisIncrementalCompileOutput {
  // Return warm output with cache reuse counters and next block cache state.
  return { result, cache: { blocks: nextBlocks, parseCache }, stats: { mode, totalBlocks: preparedLeaves.allLeaves.length, reusedBlocks, renderedBlocks: preparedLeaves.allLeaves.length - reusedBlocks, parser } }
}

// Build one observed-rule payload from a leaf and optional cached override.
function buildObservedRule(
  leaf: IncrementalCompileResultContext['preparedLeaves']['observedLeaves'][number],
  ruleOverridesByKey?: Map<string, string>,
): LuisCompileObservedRule {
  // Return the observed-rule payload with override or freshly rendered CSS.
  return { key: leaf.key, selector: leaf.selector, atRules: [...leaf.atRules], line: leaf.sourceLine, rule: ruleOverridesByKey?.get(leaf.key) ?? renderLeafRule(leaf) }
}

// Check the final CSS-size budget and fail hard or soft as configured.
function enforceCssSizeBudget(
  result: LuisCompileResult,
  sourceId: string,
  resourcePolicy: IncrementalCompileResultContext['resourcePolicy'],
  maxOutputCssChars: number,
): void {
  // Check whether the final CSS output exceeds the configured size budget.
  if (result.css.length > maxOutputCssChars) {
    const message = `[${sourceId}] CSS size budget exceeded (${result.css.length}/${maxOutputCssChars} chars).`
    if (resourcePolicy === 'fail-closed') {
      throwResourceLimitError(message)
    } else {
      result.warnings.push(buildResourceLimitWarning(1, message))
    }
  }
}
